import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Snackbar } from "@mui/material";
import QualificationSection, { buildQualificationSummary, DEFAULT_QUAL_RULE, QualificationRule } from "../../../staffCaregiverHiring/careJobs/components/qualificationSection";
import ScheduleSection, { DEFAULT_SCHEDULE_RULE, ScheduleRule } from "../../../staffCaregiverHiring/careJobs/components/scheduleSection";
import React from "react";
import JobPostForm from "../../../staffCaregiverHiring/careJobs/components/jobPostForm";

/** ========================================================================
 * Post/Edit dialog wrapper
 * - Adds QualificationSection above JobPostForm
 * - Copies summary to clipboard and emits a CustomEvent
 *   ("plany:add-qual-summary") so JobPostForm can optionally listen and append
 * ====================================================================== */
export function JobPostDialog({
  open,
  onClose,
  businessId,
  initialJob,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  businessId: number | null;
  initialJob?: any | null;
  onSaved?: () => void;
}) {
  const [qualReq, setQualReq] =
    React.useState<QualificationRule>(DEFAULT_QUAL_RULE);
  const [snackOpen, setSnackOpen] = React.useState(false);
  const [schedule, setSchedule] = React.useState<ScheduleRule>(
    DEFAULT_SCHEDULE_RULE
  );

  const handleInsert = async () => {
    const summary = buildQualificationSummary(qualReq);

    // 1) Copy to clipboard so the poster can paste into Description immediately
    try {
      await navigator.clipboard.writeText(summary);
      setSnackOpen(true);
    } catch {
      // ignore if clipboard blocked
    }

    // 2) Emit a custom event so JobPostForm can optionally listen and append
    //    (purely optional – if your JobPostForm doesn’t listen, the user can paste)
    try {
      const evt = new CustomEvent("plany:add-qual-summary", {
        detail: { summary },
      });
      window.dispatchEvent(evt);
    } catch {
      // no-op in SSR/older browsers
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{initialJob ? "Edit Job" : "Post a Job"}</DialogTitle>

      <DialogContent dividers>
        {/* QUALIFICATIONS HELPER (new) */}
        <Alert severity="info" sx={{ mb: 2 }}>
          Use the helper below to craft a clear <strong>Qualifications</strong>{" "}
          line. Click <em>Insert into description</em> to copy it (and notify
          the form).
        </Alert>

        <Box sx={{ mb: 2 }}>
          <QualificationSection
            value={qualReq}
            onChange={setQualReq}
            onGenerateSummary={handleInsert}
          />
        </Box>

        <ScheduleSection
          value={schedule}
          onChange={setSchedule}
          onGenerateSummary={(summary) => {
            // Copy to clipboard and notify JobPostForm (optional auto-append)
            navigator.clipboard.writeText(summary).catch(() => { });
            window.dispatchEvent(
              new CustomEvent("plany:add-schedule-summary", {
                detail: { summary },
              })
            );
          }}
        />

        {/* ORIGINAL FORM */}
        {businessId ? (
          <JobPostForm
            businessId={businessId}
            initialValues={initialJob || undefined}
            // OPTIONAL: If your JobPostForm wants to auto-append on event,
            // you can add inside it:
            //   React.useEffect(() => {
            //     const h = (e: any) => setDescription(prev => appendSummary(prev, e.detail.summary));
            //     window.addEventListener("plany:add-qual-summary", h);
            //     return () => window.removeEventListener("plany:add-qual-summary", h);
            //   }, []);
            onSaved={onSaved}
          />
        ) : (
          <Alert severity="warning" sx={{ mt: 2 }}>
            No business selected.
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      <Snackbar
        open={snackOpen}
        autoHideDuration={2500}
        onClose={() => setSnackOpen(false)}
        message="Qualifications summary copied. Paste it into Description."
      />
    </Dialog>
  );
}